import React, { useCallback, useEffect, useState } from 'react';
import { FaEye, FaFileCsv, FaMobileAlt, FaPaperPlane, FaDesktop } from 'react-icons/fa';
import { httpAdmin } from '../lib/http';
import { ConfirmationDialog, useGuard401 } from './adminShared';
import './ShopOwnerOutreach.css';

const EMPTY_FORM = { firstName: '', email: '', template: 'intro', cc: '', bcc: '' };

function parseCsv(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [firstName = '', email = ''] = line.split(',').map((value) => value.trim());
      if (index === 0 && /first.?name/i.test(firstName) && /email/i.test(email)) return null;
      return { firstName, email };
    })
    .filter((recipient) => recipient && recipient.firstName && recipient.email)
    .slice(0, 50);
}

const ShopOwnerOutreach = () => {
  const guard = useGuard401();
  const [form, setForm] = useState(EMPTY_FORM);
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState({ html: '', subject: '' });
  const [history, setHistory] = useState([]);
  const [csvRecipients, setCsvRecipients] = useState([]);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirm, setConfirm] = useState({ isOpen: false, type: null });

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const response = await httpAdmin.getJson('/api/admin/outreach/shop-owner/history?page=1&limit=20');
      setHistory(response.items || []);
    } catch (err) {
      if (!guard(err)) setError(err?.message || 'Failed to load outreach history');
    } finally {
      setLoadingHistory(false);
    }
  }, [guard]);

  const loadPreview = useCallback(async () => {
    try {
      setLoadingPreview(true);
      const response = await httpAdmin.postJson('/api/admin/outreach/shop-owner/preview', {
        firstName: form.firstName,
        template: form.template,
      });
      setPreview({ html: response.html || '', subject: response.subject || '' });
    } catch (err) {
      if (!guard(err)) setError(err?.message || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [form.firstName, form.template, guard]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await httpAdmin.getJson('/api/admin/outreach/shop-owner/templates');
        setTemplates(response.templates || []);
      } catch (err) {
        if (!guard(err)) setError(err?.message || 'Failed to load outreach templates');
      }
    };
    loadInitialData();
    loadHistory();
  }, [guard, loadHistory]);

  useEffect(() => {
    const timer = setTimeout(loadPreview, 350);
    return () => clearTimeout(timer);
  }, [loadPreview]);

  const handleCsvFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const recipients = parseCsv(await file.text());
    setCsvRecipients(recipients);
    setError(recipients.length ? '' : 'CSV needs firstName,email rows.');
  };

  const requestSend = (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!form.firstName.trim() || !form.email.trim()) {
      setError('Enter a first name and email address.');
      return;
    }
    setConfirm({ isOpen: true, type: 'single' });
  };

  const requestBulkSend = () => {
    setError('');
    setSuccess('');
    if (!csvRecipients.length) {
      setError('Upload a CSV with at least one valid recipient first.');
      return;
    }
    setConfirm({ isOpen: true, type: 'bulk' });
  };

  const sendConfirmed = async () => {
    const type = confirm.type;
    setConfirm({ isOpen: false, type: null });
    try {
      setSending(true);
      if (type === 'single') {
        const response = await httpAdmin.postJson('/api/admin/outreach/shop-owner/send', {
          firstName: form.firstName.trim(),
          email: form.email.trim(),
          template: form.template,
          cc: form.cc,
          bcc: form.bcc,
        });
        setSuccess(response.message || `Email sent to ${form.email}`);
        setForm((current) => ({ ...current, email: '' }));
      } else {
        const response = await httpAdmin.postJson('/api/admin/outreach/shop-owner/bulk', {
          recipients: csvRecipients,
          template: form.template,
          cc: form.cc,
          bcc: form.bcc,
        });
        setSuccess(`${response.sent} sent, ${response.failed} failed.`);
        setCsvRecipients([]);
      }
      await loadHistory();
    } catch (err) {
      if (!guard(err)) setError(err?.message || 'Failed to send outreach');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="adm-page adm-page--outreach">
      <header className="adm-page-head outreach-page-head">
        <div>
          <h2>Shop Owner Outreach</h2>
          <p>Send branded, personalized Ehgezly outreach and track delivery from one place.</p>
        </div>
      </header>

      {error && <div className="outreach-alert outreach-alert-error" role="alert">{error}</div>}
      {success && <div className="outreach-alert outreach-alert-success" role="status">{success}</div>}

      <div className="outreach-layout">
        <div className="outreach-form-panel">
          <form className="outreach-form" onSubmit={requestSend}>
            <div className="outreach-fields-row">
              <label className="outreach-form-group">
                <span>First name</span>
                <input name="firstName" value={form.firstName} onChange={updateForm} placeholder="e.g. Ahmed" autoComplete="given-name" />
              </label>
              <label className="outreach-form-group">
                <span>Email address</span>
                <input name="email" type="email" value={form.email} onChange={updateForm} placeholder="shopowner@example.com" autoComplete="email" />
              </label>
            </div>

            <label className="outreach-form-group outreach-form-group--full">
              <span>Template</span>
              <select name="template" value={form.template} onChange={updateForm}>
                {(templates.length ? templates : [{ id: 'intro', label: 'Introduction' }]).map((template) => (
                  <option key={template.id} value={template.id}>{template.label}</option>
                ))}
              </select>
              <small>{templates.find((template) => template.id === form.template)?.description}</small>
            </label>

            <details className="outreach-recipients-extra">
              <summary>Optional CC / BCC</summary>
              <div className="outreach-fields-row">
                <label className="outreach-form-group">
                  <span>CC</span>
                  <input name="cc" type="text" value={form.cc} onChange={updateForm} placeholder="team@example.com" />
                </label>
                <label className="outreach-form-group">
                  <span>BCC</span>
                  <input name="bcc" type="text" value={form.bcc} onChange={updateForm} placeholder="manager@example.com" />
                </label>
              </div>
              <small>Separate multiple addresses with commas.</small>
            </details>

            <div className="outreach-subject-box">
              <span>Subject</span>
              <strong>{preview.subject || 'Get More Bookings for Your Salon | Ehgezly'}</strong>
            </div>

            <div className="outreach-actions-row">
              <button type="button" className="outreach-btn outreach-btn-secondary" onClick={loadPreview} disabled={loadingPreview}>
                <FaEye aria-hidden="true" />{loadingPreview ? 'Refreshing…' : 'Refresh preview'}
              </button>
              <button type="submit" className="outreach-btn outreach-btn-primary" disabled={sending}>
                <FaPaperPlane aria-hidden="true" />{sending ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </form>

          <div className="outreach-bulk">
            <div>
              <h3>Bulk send</h3>
              <p>Upload a CSV with <code>firstName,email</code> rows (maximum 50).</p>
            </div>
            <label className="outreach-file-input">
              <FaFileCsv aria-hidden="true" />
              <span>Choose CSV</span>
              <input type="file" accept=".csv,text/csv" onChange={handleCsvFile} />
            </label>
            <div className="outreach-bulk-footer">
              <span>{csvRecipients.length ? `${csvRecipients.length} recipient${csvRecipients.length === 1 ? '' : 's'} ready` : 'No CSV selected'}</span>
              <button type="button" className="outreach-btn outreach-btn-primary" onClick={requestBulkSend} disabled={sending || !csvRecipients.length}>
                <FaPaperPlane aria-hidden="true" /> Send bulk
              </button>
            </div>
          </div>
        </div>

        <div className="outreach-preview-panel">
          <div className="outreach-preview-header">
            <span><FaEye aria-hidden="true" /> Live preview {loadingPreview && '· Updating…'}</span>
            <div className="outreach-preview-toggle" role="group" aria-label="Preview width">
              <button type="button" className={previewMode === 'desktop' ? 'is-active' : ''} onClick={() => setPreviewMode('desktop')} aria-pressed={previewMode === 'desktop'}><FaDesktop aria-hidden="true" />Desktop</button>
              <button type="button" className={previewMode === 'mobile' ? 'is-active' : ''} onClick={() => setPreviewMode('mobile')} aria-pressed={previewMode === 'mobile'}><FaMobileAlt aria-hidden="true" />Mobile</button>
            </div>
          </div>
          <div className={`outreach-preview-frame-wrap ${previewMode === 'mobile' ? 'outreach-preview-frame-wrap--mobile' : ''}`}>
            {preview.html ? <iframe title="Email preview" className={previewMode === 'mobile' ? 'outreach-preview-frame outreach-preview-frame--mobile' : 'outreach-preview-frame'} srcDoc={preview.html} sandbox="" /> : <p className="outreach-preview-empty">Preparing the email preview…</p>}
          </div>
        </div>
      </div>

      <section className="outreach-history" aria-labelledby="outreach-history-title">
        <div className="outreach-history-head">
          <div><h3 id="outreach-history-title">Server history</h3><p>Recent sends for all administrators.</p></div>
          <button type="button" className="outreach-btn outreach-btn-secondary" onClick={loadHistory} disabled={loadingHistory}>Refresh</button>
        </div>
        <div className="outreach-history-table-wrap">
          <table>
            <thead><tr><th>Recipient</th><th>Template</th><th>Status</th><th>Sent</th></tr></thead>
            <tbody>
              {history.map((entry) => <tr key={entry._id}><td><strong>{entry.firstName}</strong><span>{entry.email}</span></td><td>{entry.template}</td><td><span className={`outreach-status outreach-status--${entry.status}`}>{entry.status}</span>{entry.error && <small>{entry.error}</small>}</td><td>{new Date(entry.createdAt).toLocaleString()}</td></tr>)}
              {!loadingHistory && !history.length && <tr><td colSpan="4" className="outreach-history-empty">No outreach sends recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <ConfirmationDialog
        isOpen={confirm.isOpen}
        onClose={() => setConfirm({ isOpen: false, type: null })}
        onConfirm={sendConfirmed}
        title={confirm.type === 'bulk' ? 'Send bulk outreach?' : 'Send outreach email?'}
        message={confirm.type === 'bulk' ? `Send the selected template to ${csvRecipients.length} recipients?` : `Send the selected template to ${form.email}?`}
        confirmText={confirm.type === 'bulk' ? 'Send bulk email' : 'Send email'}
      />
    </section>
  );
};

export default ShopOwnerOutreach;
